// ========= START: bp-tango-dev/src/App.js (QR SCANNER & SAVED SETS UPDATE) =========
import React, { useState, useEffect } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner'; // Requires: npm install @yudiel/react-qr-scanner
import './App.css';
import './BPTango.css';
import TangoWelcomeScreen from './components/TangoWelcomeScreen';
import TangoSetupScreen from './components/TangoSetupScreen';
import TangoPracticeScreen from './components/TangoPracticeScreen';
import CustomModal from './components/CustomModal';
import vocabulary from './data/pronunciation_vocab.js';

function App() {
  const [appPhase, setAppPhase] = useState('WELCOME');

  // Practice Settings
  const [practiceSettings, setPracticeSettings] = useState({
    grade: 3,
    categories: [],
    words: [],
  });

  // Setup Selections
  const [setupSelections, setSetupSelections] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // --- NEW: Saved Sets & Scanner State ---
  const [savedSets, setSavedSets] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showSetsMenu, setShowSetsMenu] = useState(false);

  // Load saved sets from local storage on startup
  useEffect(() => {
    const loadedSets = localStorage.getItem('bp_tango_saved_sets');
    if (loadedSets) {
      setSavedSets(JSON.parse(loadedSets));
    }
  }, []);

  // Save sets to local storage whenever they change
  useEffect(() => {
    localStorage.setItem('bp_tango_saved_sets', JSON.stringify(savedSets));
  }, [savedSets]);

  // --- Main Navigation Handlers ---

  const handleStartPractice = () => {
    if (!setupSelections || setupSelections.categories.length === 0) {
      setShowErrorModal(true);
      return;
    }

    const practiceWords = vocabulary.filter(word =>
      word.grade <= setupSelections.grade && setupSelections.categories.includes(word.category)
    );

    if (practiceWords && practiceWords.length > 0) {
      setPracticeSettings({
        grade: setupSelections.grade,
        categories: setupSelections.categories,
        words: practiceWords
      });
      setAppPhase('PRACTICE');
    } else {
      console.error("Attempted to start practice with no words.");
    }
  };

  const handleEndPractice = () => {
    setAppPhase('SETUP');
  };

  const handleGoBack = () => {
    setAppPhase('WELCOME');
  };

  // --- NEW: Scanner & Set Logic ---

  const handleScanSuccess = (detectedCodes) => {
    if (!detectedCodes || detectedCodes.length === 0) return;

    // The library returns an array of objects. We grab the rawValue of the first one.
    const rawData = detectedCodes[0].rawValue;

    try {
      const parsedData = JSON.parse(rawData);

      if (parsedData.words && Array.isArray(parsedData.words)) {
        // 1. Create the new set object
        // Use provided name or fallback to Date
        const newSetName = parsedData.name || new Date().toISOString().split('T')[0];

        const newSet = {
          id: Date.now(), // Unique ID for the set
          name: newSetName,
          wordIds: parsedData.words,
          date: new Date().toLocaleDateString()
        };

        // 2. Save to state (and localStorage via effect)
        setSavedSets(prev => [newSet, ...prev]);

        // 3. Close scanner
        setShowScanner(false);

        // 4. Immediately start practice
        loadSetForPractice(newSet);
      }
    } catch (e) {
      console.error("Failed to parse QR code data", e);
      alert("Invalid QR Code format.");
    }
  };

  const loadSetForPractice = (set) => {
    // Find the actual word objects from the IDs
    const setWords = vocabulary.filter(w => set.wordIds.includes(w.id));

    if (setWords.length === 0) {
      alert("Could not find any matching words in the database.");
      return;
    }

    // Extract unique categories from these words to set up the context
    const uniqueCategories = [...new Set(setWords.map(w => w.category))];

    setPracticeSettings({
      grade: 0, // Custom sets ignore grade limits
      categories: uniqueCategories,
      words: setWords
    });

    setShowSetsMenu(false); // Close menu if open
    setAppPhase('PRACTICE');
  };

  const handleDeleteSet = (setId) => {
    if (window.confirm("Are you sure you want to delete this set?")) {
      setSavedSets(prev => prev.filter(s => s.id !== setId));
    }
  };

  const handleRenameSet = (setId) => {
    const set = savedSets.find(s => s.id === setId);
    const newName = prompt("Enter new name:", set.name);
    if (newName && newName.trim() !== "") {
      setSavedSets(prev => prev.map(s => s.id === setId ? { ...s, name: newName } : s));
    }
  };

  const renderCurrentPhase = () => {
    switch (appPhase) {
      case 'WELCOME':
        return <TangoWelcomeScreen onStart={() => setAppPhase('SETUP')} />;

      case 'SETUP':
        const isStartDisabled = !setupSelections || setupSelections.categories.length === 0;
        return (
          <>
            <video autoPlay loop muted className="setup-video-bg">
              <source src={`${process.env.PUBLIC_URL}/assets/backgrounds/setup-bg.mp4`} type="video/mp4" />
            </video>

            {showErrorModal && (
              <CustomModal
                message="Please select at least one category to practice!"
                onClose={() => setShowErrorModal(false)}
              />
            )}

            {/* --- NEW: Scanner Modal --- */}
            {showScanner && (
              <div className="modal-overlay">
                <div className="scanner-modal-content">
                  <h2>Scan Teacher's QR Code</h2>
                  <div className="scanner-container">
                    <Scanner
                      onScan={handleScanSuccess}
                      formats={['qr_code']}
                      components={{ audio: false }} // Optional: disable beep
                    />
                  </div>
                  <button className="close-scanner-btn" onClick={() => setShowScanner(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* --- NEW: Saved Sets Menu Modal --- */}
            {showSetsMenu && (
              <div className="modal-overlay" onClick={() => setShowSetsMenu(false)}>
                <div className="sets-menu-content" onClick={e => e.stopPropagation()}>
                  <div className="sets-menu-header">
                    <h2>Saved Sets</h2>
                    <button className="close-sets-btn" onClick={() => setShowSetsMenu(false)}>&times;</button>
                  </div>
                  <div className="sets-list">
                    {savedSets.length === 0 ? (
                      <p className="no-sets-msg">No saved sets yet. Use "Get Set" to scan one!</p>
                    ) : (
                      savedSets.map(set => (
                        <div key={set.id} className="saved-set-item">
                          <div className="set-info">
                            <span className="set-name">{set.name}</span>
                            <span className="set-details">{set.wordIds.length} words • {set.date}</span>
                          </div>
                          <div className="set-actions">
                            <button className="set-action-btn play" onClick={() => loadSetForPractice(set)}>Play ▶</button>
                            <button className="set-action-btn rename" onClick={() => handleRenameSet(set.id)}>✏️</button>
                            <button className="set-action-btn delete" onClick={() => handleDeleteSet(set.id)}>🗑</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            <header className="tango-header">
              <div className="header-side-container">
                <button className="header-nav-button" onClick={handleGoBack}>
                  &lt; Back
                </button>
                {/* --- NEW: Menu Button (Right of Back) --- */}
                <button className="header-nav-button menu-button" onClick={() => setShowSetsMenu(true)}>
                  📂 Sets
                </button>
              </div>
              <h1>BP Tango - Setup</h1>
              <div className="header-side-container">
                {/* --- NEW: Get Set Button (Left of Start) --- */}
                <button className="header-nav-button get-set-button" onClick={() => setShowScanner(true)}>
                  📷 Get Set
                </button>
                <button
                  className="tango-start-button header-button"
                  onClick={handleStartPractice}
                  disabled={isStartDisabled}
                >
                  Start Practice
                </button>
              </div>
            </header>
            <main className="tango-main-content">
              <TangoSetupScreen onSelectionChange={setSetupSelections} />
            </main>
          </>
        );

      case 'PRACTICE':
        return <TangoPracticeScreen settings={practiceSettings} onEndPractice={handleEndPractice} />;

      default:
        return <TangoWelcomeScreen onStart={() => setAppPhase('SETUP')} />;
    }
  };

  // ========= START: src/App.js - RENDER SECTION =========
  return (
    // Added onContextMenu to disable right-click across the whole app
    <div className="App" onContextMenu={(e) => e.preventDefault()}>
      {renderCurrentPhase()}
    </div>
  );
}

export default App;
// ========= END: src/App.js - RENDER SECTION =========